import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { ArrowRightIcon, TranslateIcon } from "@phosphor-icons/react";
import { Link, useRouteContext } from "@tanstack/react-router";
import { motion, useMotionValue, useSpring } from "motion/react";
import { useEffect, useRef } from "react";

import { LocaleCombobox } from "@/components/locale/combobox";
import { ThemeToggleButton } from "@/components/theme/toggle-button";
import { BrandIcon } from "@/components/ui/brand-icon";
import { Button } from "@/components/ui/button";
import { getMultiResumePublicChromeCopy } from "@/utils/multiresume-brand";

import { getHomeHeaderCta } from "../-navigation";

export function Header() {
  const { i18n } = useLingui();
  const publicChromeCopy = getMultiResumePublicChromeCopy();
  const { session } = useRouteContext({ strict: false });
  const headerCta = getHomeHeaderCta(session);
  const y = useMotionValue(0);
  const lastScroll = useRef(0);
  const ticking = useRef(false);
  const springY = useSpring(y, { stiffness: 300, damping: 40 });

  useEffect(() => {
    if (typeof window === "undefined") return;

    function onScroll() {
      const current = window.scrollY ?? 0;
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          if (current > 32 && current > lastScroll.current) {
            // Scrolling down, hide
            y.set(-100);
          } else {
            // Scrolling up, show
            y.set(0);
          }
          lastScroll.current = current;
          ticking.current = false;
        });
        ticking.current = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [y]);

  return (
    <motion.header
      style={{ y: springY }}
      className="fixed inset-x-0 top-0 z-50 border-b border-transparent bg-background/80 backdrop-blur-lg transition-colors"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      <nav
        aria-label={i18n._("Main navigation")}
        className="container mx-auto flex min-w-0 items-center gap-x-3 p-3 lg:px-12"
      >
        <Link
          to="/"
          className="transition-opacity hover:opacity-80"
          aria-label={i18n._(msg`MultiResume - Go to homepage`)}
        >
          <BrandIcon className="h-9 w-auto" />
        </Link>

        <div className="ml-auto flex shrink-0 items-center gap-x-2">
          <LocaleCombobox
            ordinaryOnly
            render={
              <Button size="icon" variant="ghost" aria-label={i18n._("Change language")}>
                <TranslateIcon />
              </Button>
            }
          />

          <ThemeToggleButton />

          <Button
            variant="ghost"
            nativeButton={false}
            className="hidden sm:inline-flex"
            render={<Link to="/pricing">{publicChromeCopy.pricing}</Link>}
          />

          <Button
            nativeButton={false}
            aria-label={i18n._(headerCta.label)}
            render={
              <Link to={headerCta.to}>
                <span className="hidden sm:inline">
                  {session ? <Trans>Go to dashboard</Trans> : <Trans>Sign in</Trans>}
                </span>
                <ArrowRightIcon aria-hidden="true" />
              </Link>
            }
          />
        </div>
      </nav>
    </motion.header>
  );
}
